<!-- RulesPage.vue -->
<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Layout from '../components/layout/Layout.vue'
import StellarBackground from '../components/animations/StellarBackground.vue'
import CyberLoading from '../components/ui/CyberLoading.vue'

const { t } = useI18n()
const isLoading = ref(true)

onMounted(() => {
  setTimeout(() => {
    isLoading.value = false
  }, 1000)
})
</script>

<template>
  <Layout>
    <div class="rules-wrapper">
      <StellarBackground backColor="#2d3434" :circle-offset="{x: -350, y: 100}"/>
      <div class="rules-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="rules-page" :class="{ 'content-loaded': !isLoading }">
          <!-- Header Section -->
          <div class="rules-header">
            <h1>{{ t('information.rules.title') }}</h1>
            <p>{{ t('information.rules.subtitle') }}</p>
          </div>

          <!-- Main Content -->
          <div class="content-section">
            <!-- Not Allowed Section -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-cancel</v-icon>
                {{ t('information.rules.notAllowed.title') }}
              </h2>
              
              <!-- Drugs & Alcohol -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.drugs.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.drugs.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Illegal Activities -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.illegal.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.illegal.content')"></div>
                </v-card-text>
              </v-card>

              <!-- External Catering -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.catering.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.catering.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Smoking -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.smoking.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.smoking.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Behavior -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.behavior.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.behavior.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Noise -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.noise.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.noise.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Network -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.network.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.network.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Commercial -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.notAllowed.sections.commercial.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.notAllowed.sections.commercial.content')"></div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Expected Section -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-check-circle</v-icon>
                {{ t('information.rules.expected.title') }}
              </h2>

              <!-- Cleanliness -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.cleanliness.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.cleanliness.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Respect -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.respect.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.respect.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Accounts -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.accounts.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.accounts.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Social -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.social.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.social.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Instructions -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.instructions.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.instructions.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Health -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.health.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.health.content')"></div>
                </v-card-text>
              </v-card>

              <!-- Participation -->
              <v-card class="content-card" elevation="0">
                <v-card-title class="subsection-title">
                  {{ t('information.rules.expected.sections.participation.title') }}
                </v-card-title>
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.expected.sections.participation.content')"></div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Footer Section -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-gamepad-variant</v-icon>
                {{ t('information.rules.footer.title') }}
              </h2>
              <v-card class="content-card" elevation="0">
                <v-card-text>
                  <div class="content-text" v-html="t('information.rules.footer.content')"></div>
                </v-card-text>
              </v-card>
            </div>

            <div class="bottom-links d-none d-md-flex">
              <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
              <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
              <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.rules-wrapper {
  position: relative;
  width: 100%;
  overflow-x: hidden;
  background-color: #1a1a1a;
  padding: 2rem 0;
}

.rules-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  overflow-y: auto;
  min-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
}

@media (max-width: 500px) {
  .rules-container {
    padding: 0 1rem; 
  }
}

.rules-page {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.rules-header {
  text-align: center;
  position: relative;
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.rules-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.rules-header p {
  font-size: 1.2rem;
  color: #ccc;
  margin: 1rem auto 0;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
  max-width: 1200px;
}

.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 4rem;
  margin-bottom: 4rem;
}

.section-container {
  width: 100%;
  max-width: 1200px;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-family: var(--font-secondary);
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.section-icon {
  margin-right: 1rem;
}

.content-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: box-shadow 0.3s ease;
  margin-bottom: 1rem;
}

.content-card:hover {
  box-shadow: 0 8px 40px rgba(118, 196, 174, 0.1);
}

.subsection-title {
  color: #76c4ae !important;
  font-size: 1.2rem !important;
  padding: 1rem !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.content-text {
  margin-top: 1rem;
  text-align: left;
  line-height: 1.8;
}

.content-text :deep(p) {
  margin-bottom: 1rem;
}

.content-text :deep(ul) {
  list-style-type: none;
  padding: 0;
}

.content-text :deep(li) {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.content-text :deep(li)::before {
  content: "•";
  color: #76c4ae;
  position: absolute;
  left: 0;
  font-size: 1.2em;
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 0;
  margin-top: 2rem;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

.nav-link :deep(br) {
  display: block;
  content: "";
  margin: 0.1rem 0;
}

@media (max-width: 768px) {
  .rules-header h1 {
    font-size: 1.5rem;
  }

  .rules-header p {
    font-size: 1rem;
    padding: 0 1rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .subsection-title {
    font-size: 1.1rem !important;
  }

  .content-text {
    font-size: 1rem;
  }

  .content-card {
    padding: 1rem;
  }

  .section-container {
    padding: 0 1rem;
  }

  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }

  .content-section {
    gap: 3rem;
  }
}
</style>
